import { Button } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown'
import _ from 'lodash'
import { FC, SetStateAction, useCallback, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import styled from 'styled-components'
import { GenericPanelData } from 'types'

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  position: relative;
  min-height: 120px;
  padding: 5px;
  box-sizing: border-box;
`

const ButtonContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  opacity: 0.7;
`

const MarkdownContent = styled.div`
  width: 100%;
  text-align: left;
  box-sizing: border-box;
  overflow-x: scroll;
`

export interface MarkdownPanelData extends GenericPanelData {
  content: string
}

export const LATEST_MARKDOWN_PANEL_DATA_SCHEMA_VERSION = 1

export const DEFAULT_MARKDOWN_PANEL_DATA: MarkdownPanelData = {
  panelDataSchemaVersion: LATEST_MARKDOWN_PANEL_DATA_SCHEMA_VERSION,
  title: 'Markdown',
  isPinned: true,
  content:
    '# Markdown Panel\n\n You can edit the content by clicking the "Edit" button.',
}

export const MarkdownPanel: FC = () => {
  const {
    panelData: { content },
    setPanelData,
  } = useVersionAwareDrilldownPanelData<MarkdownPanelData>()

  const [isEditModeEnabled, setIsEditModeEnabled] = useState(false)

  const toggleEditMode = () => {
    setIsEditModeEnabled(!isEditModeEnabled)
  }

  const setPanelContent = useCallback(
    (val: SetStateAction<string>) => {
      setPanelData((prev) => {
        const newVal = typeof val === 'function' ? val(prev.content) : val
        if (_.isEqual(newVal, prev.content)) {
          return prev
        }
        return {
          ...prev,
          content: newVal,
        }
      })
    },
    [setPanelData]
  )

  const handleContentChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setPanelContent(event.target.value)
  }

  return (
    <PanelContainer>
      <ButtonContainer>
        <Button type='primary' variant='outlined' onClick={toggleEditMode}>
          {isEditModeEnabled ? 'View' : 'Edit'}
        </Button>
      </ButtonContainer>
      {isEditModeEnabled ? (
        <TextArea
          style={{ height: '100%', minHeight: '200px' }}
          value={content}
          onChange={handleContentChange}
        />
      ) : (
        <MarkdownContent>
          <ReactMarkdown>{content}</ReactMarkdown>
        </MarkdownContent>
      )}
    </PanelContainer>
  )
}
